import React from 'react';
import { motion } from 'framer-motion';

const renderDecorativeTicks = (count, className) => (
  Array.from({ length: count }, (_, index) => (
    <span key={index} className={className} />
  ))
);

const RibbonCardObject = ({ opened }) => (
  <div className="lumirae-theme-object lumirae-theme-ribbon-card" aria-hidden="true">
    <motion.div
      className="lumirae-theme-card-shadow"
      animate={opened ? { y: 12, rotate: -2, scale: 0.96 } : { y: [0, -4, 0] }}
      transition={{ duration: opened ? 0.7 : 3.2, repeat: opened ? 0 : Infinity, ease: 'easeInOut' }}
    />
    <motion.div
      className="lumirae-theme-foil-card"
      animate={opened ? { y: -30, rotate: 1.5, scale: 1.02 } : { y: [0, -5, 0] }}
      transition={{ duration: 0.78, ease: 'easeOut' }}
    >
      <span />
      <strong>Thank You</strong>
      <em>With love</em>
    </motion.div>
    <motion.div
      className="lumirae-theme-ribbon horizontal"
      animate={opened ? { x: -92, opacity: 0.42, rotate: -8 } : { x: 0, opacity: 1 }}
      transition={{ duration: 0.74, ease: 'easeOut' }}
    />
    <motion.div
      className="lumirae-theme-ribbon vertical"
      animate={opened ? { y: 86, opacity: 0.28, scaleY: 0.62 } : { y: 0, opacity: 1 }}
      transition={{ duration: 0.74, ease: 'easeOut' }}
    />
  </div>
);

const GiftLetterObject = ({ opened }) => (
  <div className="lumirae-theme-object lumirae-theme-gift-letter" aria-hidden="true">
    <motion.div
      className="lumirae-theme-gift-letter-lid left"
      animate={opened ? { rotate: -14, x: -34, y: -52, opacity: 0.86 } : { y: [0, -5, 0] }}
      transition={{ duration: opened ? 0.76 : 3, repeat: opened ? 0 : Infinity, ease: 'easeInOut' }}
    />
    <motion.div
      className="lumirae-theme-gift-letter-lid right"
      animate={opened ? { rotate: 14, x: 34, y: -52, opacity: 0.86 } : { y: [0, -5, 0] }}
      transition={{ duration: opened ? 0.76 : 3, repeat: opened ? 0 : Infinity, ease: 'easeInOut' }}
    />
    <div className="lumirae-theme-gift-letter-box" />
    <span className="lumirae-theme-gift-letter-ribbon vertical" />
    <span className="lumirae-theme-gift-letter-ribbon horizontal" />
    <motion.div
      className="lumirae-theme-gift-letter-paper"
      animate={opened ? { y: -72, rotate: -1.5, opacity: 1, scale: 1.04 } : { y: 12, opacity: 0.62, scale: 0.92 }}
      transition={{ duration: 0.82, delay: opened ? 0.12 : 0, ease: 'easeOut' }}
    >
      <span />
      <strong>For You</strong>
      <em>With love</em>
    </motion.div>
  </div>
);

const FilmEnvelopeObject = ({ opened }) => (
  <div className="lumirae-theme-object lumirae-theme-film-envelope" aria-hidden="true">
    <motion.div
      className="lumirae-theme-envelope-flap"
      animate={opened ? { rotateX: -128, y: -20 } : { rotateX: [0, -7, 0] }}
      transition={{ duration: opened ? 0.85 : 3.1, repeat: opened ? 0 : Infinity, ease: 'easeInOut' }}
    />
    <div className="lumirae-theme-envelope-pocket" />
    <motion.div
      className="lumirae-theme-film-strip"
      animate={opened ? { y: -44, x: 12, rotate: -4, opacity: 1 } : { y: 18, opacity: 0.72 }}
      transition={{ duration: 0.86, ease: 'easeOut' }}
    >
      {renderDecorativeTicks(5, 'lumirae-theme-film-frame')}
    </motion.div>
  </div>
);

const BabyAlbumObject = ({ opened }) => (
  <div className="lumirae-theme-object lumirae-theme-baby-album" aria-hidden="true">
    <motion.div
      className="lumirae-theme-album-page left"
      animate={opened ? { rotateY: -32, x: -8 } : { rotateY: [0, -8, 0] }}
      transition={{ duration: opened ? 0.78 : 3.4, repeat: opened ? 0 : Infinity, ease: 'easeInOut' }}
    />
    <motion.div
      className="lumirae-theme-album-page right"
      animate={opened ? { rotateY: 28, x: 8 } : { rotateY: [0, 8, 0] }}
      transition={{ duration: opened ? 0.78 : 3.4, repeat: opened ? 0 : Infinity, ease: 'easeInOut' }}
    />
    {[0, 1, 2].map((card) => (
      <motion.span
        key={card}
        className={`lumirae-theme-growth-card card-${card}`}
        animate={opened ? { y: -40 - card * 10, rotate: (card - 1) * 9, opacity: 1 } : { y: 8, opacity: 0 }}
        transition={{ duration: 0.56, delay: 0.16 + card * 0.08, ease: 'easeOut' }}
      />
    ))}
  </div>
);

const MemoryBoxObject = ({ opened }) => (
  <div className="lumirae-theme-object lumirae-theme-memory-box" aria-hidden="true">
    <motion.div
      className="lumirae-theme-box-lid"
      animate={opened ? { rotateX: -112, y: -24 } : { rotateX: [0, -5, 0] }}
      transition={{ duration: opened ? 0.9 : 3.4, repeat: opened ? 0 : Infinity, ease: 'easeInOut' }}
    />
    <div className="lumirae-theme-box-base" />
    <motion.div
      className="lumirae-theme-soft-album"
      animate={opened ? { y: -42, opacity: 1, scale: 1 } : { y: 12, opacity: 0.54, scale: 0.92 }}
      transition={{ duration: 0.82, ease: 'easeOut' }}
    >
      <span />
      <span />
      <span />
    </motion.div>
  </div>
);

const PolaroidMapObject = ({ opened }) => (
  <div className="lumirae-theme-object lumirae-theme-polaroid-map" aria-hidden="true">
    <motion.div
      className="lumirae-theme-camera"
      animate={opened ? { y: -12, rotate: -2 } : { y: [0, -4, 0] }}
      transition={{ duration: opened ? 0.58 : 3, repeat: opened ? 0 : Infinity, ease: 'easeInOut' }}
    >
      <span className="lens" />
      <span className="flash" />
    </motion.div>
    <motion.div
      className="lumirae-theme-polaroid-print"
      animate={opened ? { y: 58, rotate: 4, opacity: 1 } : { y: 10, opacity: 0.66 }}
      transition={{ duration: 0.76, ease: 'easeOut' }}
    />
    <motion.div
      className="lumirae-theme-map-fold"
      animate={opened ? { y: 82, scaleX: 1, opacity: 1 } : { y: 58, scaleX: 0.62, opacity: 0 }}
      transition={{ duration: 0.72, delay: 0.1, ease: 'easeOut' }}
    >
      {renderDecorativeTicks(4, 'map-pin')}
    </motion.div>
  </div>
);

export const ThemeObject = ({ variant, opened }) => {
  if (variant === 'ribbon-card') return <RibbonCardObject opened={opened} />;
  if (variant === 'gift-letter') return <GiftLetterObject opened={opened} />;
  if (variant === 'film-envelope') return <FilmEnvelopeObject opened={opened} />;
  if (variant === 'baby-album') return <BabyAlbumObject opened={opened} />;
  if (variant === 'memory-box') return <MemoryBoxObject opened={opened} />;
  if (variant === 'polaroid-map') return <PolaroidMapObject opened={opened} />;
  return <RibbonCardObject opened={opened} />;
};

export const getThemeOpeningStyle = (colors = {}) => ({
  '--theme-opening-bg': colors.bg,
  '--theme-opening-surface': colors.surface,
  '--theme-opening-accent': colors.accent,
  '--theme-opening-accent-2': colors.accent2,
  '--theme-opening-ink': colors.ink,
  '--theme-opening-muted': colors.muted,
});
